/**
 * Bits shared by all 5 sections of OrdersPage. Kept tiny so each section
 * file can focus on its own data wiring instead of repeating table
 * chrome / 暂无 badges / label maps.
 */
import type { ReactNode } from 'react';
import { Pagination } from '@/components/ui/index.js';

export type Plan = 'monthly' | 'yearly' | 'unknown';

export function planLabel(plan: Plan): string {
  if (plan === 'monthly') return '月卡';
  if (plan === 'yearly') return '年卡';
  return '未知';
}

export function channelLabel(channel: 'alipay' | 'wechat' | 'ios_iap'): string {
  if (channel === 'alipay') return '支付宝';
  if (channel === 'wechat') return '微信支付';
  return 'iOS 内购';
}

const SOURCE_STYLE: Record<
  'admin_grant' | 'xianyu_email' | 'xianyu_username',
  { label: string; tone: string }
> = {
  admin_grant: {
    label: '管理员手动',
    tone: 'border-shark-700/60 bg-shark-900/30 text-shark-200',
  },
  xianyu_email: {
    label: '闲鱼·邮箱',
    tone: 'border-amber-700/60 bg-amber-900/30 text-amber-200',
  },
  xianyu_username: {
    label: '闲鱼·账号',
    tone: 'border-amber-700/60 bg-amber-900/30 text-amber-200',
  },
};

/**
 * Pagination props shared by all 5 sections. Each section paginates its own
 * stream independently (offset lives in OrdersPage, one per section), so the
 * pager wiring is identical across sections — only the `total` field differs.
 */
export interface SectionPaging {
  offset: number;
  onOffsetChange: (next: number) => void;
  pageSize: number;
}

/**
 * Footer pager for a section. Thin wrapper over the shared {@link Pagination}
 * so each section doesn't repeat the same prop hand-off. Renders nothing when
 * there's only one page (Pagination already guards on `total <= pageSize`).
 */
export function SectionPager({
  total,
  paging,
}: {
  total: number;
  paging: SectionPaging;
}): JSX.Element | null {
  return (
    <Pagination
      offset={paging.offset}
      total={total}
      pageSize={paging.pageSize}
      onChange={paging.onOffsetChange}
    />
  );
}

export function SourceBadge({
  source,
}: {
  source: 'admin_grant' | 'xianyu_email' | 'xianyu_username';
}): JSX.Element {
  const { label, tone } = SOURCE_STYLE[source];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] ${tone}`}>
      {label}
    </span>
  );
}

/**
 * Section header row — title + optional "暂无" / status chip on the right.
 * Pulls the 5 sections into a single visual rhythm.
 */
export function SectionTitle({
  icon,
  title,
  emptyHint,
  hint,
}: {
  icon: string;
  title: string;
  /** Show this gray "暂无..." chip when the section is empty. */
  emptyHint?: string | undefined;
  /** Extra contextual hint chip (e.g. yellow "非收入 · 信息性"). */
  hint?: ReactNode | undefined;
}): JSX.Element {
  return (
    <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
      <span>{icon}</span>
      <span>{title}</span>
      {emptyHint !== undefined && (
        <span className="text-xs px-2 py-0.5 rounded bg-white/[0.05] text-slate-400 font-normal">
          {emptyHint}
        </span>
      )}
      {hint !== undefined && hint}
    </h2>
  );
}

/**
 * Table wrapper — rounded border + horizontal scroll fallback. Children is
 * the raw <table>, this just provides the chrome.
 */
export function OrderTable({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="mat-matte rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">{children}</table>
      </div>
    </div>
  );
}

/**
 * "暂无数据" empty state for sections that haven't had any rows yet.
 * Style matches the rest (dashed border on a slightly recessed surface).
 */
export function SectionEmpty({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="bg-white/[0.03] border border-dashed border-white/10 rounded-xl p-8 text-center text-sm text-slate-500">
      {children}
    </div>
  );
}
